import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Loading, Tag } from '../../UI';
import ScrollArrows from '../Contribution/ScrollArrows';

import {
  WIDGET_WIKIPEDIA,
  WIDGET_WIKIPEDIA_INSPIRED_TAGS,
} from '../../../http/apollo-queries';

import { DataWikipedia, DataWikipediaInspiredTag, SocialTag } from './types';

import { NoSearchResults } from '../../NoSearchResults';
import { GridWidget } from '../types';

import { useLocation } from 'react-router-dom';
import { WikipediaCard } from './WikipediaCard';
import SearchInput from '../../Search-input/Search-input';
import { debounce, trim } from 'lodash';
import { DropdownMenu } from '../../form';

const SEARCH_TYPE = ['INSPIRED', 'CUSTOM'] as const;
const SECOND = 1000;
const MINUTE = 60 * SECOND;

export const WikipediaWidget = ({
  width,
  height,
  standAlone,
  setLastWidgetUpdateAt,
}: GridWidget & {
  search?: string;
}) => {
  const { t } = useTranslation();

  const searchType = SEARCH_TYPE;

  const [searchValue, setSearchValue] = useState<string>('');
  const [isPresetSearch, setIsPresetSearch] = useState<boolean>(true);
  const [disabledTags, setDisabledTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [typology, setTypology] = useState<(typeof SEARCH_TYPE)[number]>(
    searchType[0]
  );

  const removeSelectedTags = () => {
    setSelectedTags([]);
  };

  const handleDisabledTags = (tag: string) => {
    setDisabledTags((pr) =>
      pr.includes(tag) ? pr.filter((el) => el !== tag) : [...pr, tag]
    );
  };

  const searchTags = selectedTags.reduce(
    (arr, tag) =>
      disabledTags.includes(tag) || tag.length === 0 ? arr : [...arr, tag],
    [] as string[]
  );

  const {
    loading: loadingTags,
    data: { widgetWikipediaInspiredTags: { tags = [] } = {} } = {},
  } = useQuery<DataWikipediaInspiredTag>(WIDGET_WIKIPEDIA_INSPIRED_TAGS, {
    fetchPolicy: 'network-only',
    pollInterval: 5 * MINUTE,
    variables: { search: selectedTags.map((tag) => tag) },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const {
    loading: loadingPages,
    data: { widgetWikipedia: { items: wikipediaPages = [] } = {} } = {},
  } = useQuery<DataWikipedia>(WIDGET_WIKIPEDIA, {
    fetchPolicy: 'network-only',
    pollInterval: 5 * MINUTE,
    skip: !selectedTags.length,
    variables: { search: searchTags.map((tag) => tag) },
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const isLoading = loadingPages || loadingTags;

  useEffect(() => {
    if (tags.length && isPresetSearch) {
      setSelectedTags(tags);
      setIsPresetSearch(false);
    }
  }, [JSON.stringify(tags)]);

  const onSearchChange = useCallback(
    debounce((args) => {
      if (trim(args)) {
        setTypology('CUSTOM');
        setSelectedTags([args]);
        setSearchValue(args);
      } else {
        setSelectedTags([]);
      }
    }, 700),
    []
  );

  return (
    <>
      {!standAlone && (
        <div>
          <div className="h-14 border-b border-grey-10 flex items-center w-full dark:border-grey-80">
            <SearchInput
              onChange={onSearchChange}
              value={searchValue}
              size={'full'}
            />
          </div>

          <div
            className={`
              flex border-b-1 border-grey-10 dark:border-grey-80
            `}
          >
            <div className={'flex w-full h-16'}>
              <DropdownMenu
                dropdownClassName={`
                flex
                items-center
                border-r
                border-grey-10
                py-5
                pr-8
                pl-4
                dark:border-grey-80
              `}
                dropdownTooltipClassName={`
                bg-white
                border-solid
                border
                border-grey-10
                -mt-3
                ml-10
              `}
                dropdownTooltipXOffset={8}
                dropdownTooltipYOffset={24}
                dropdownArrowClassName="-right-7 text-black"
                dropdownItems={searchType.map((s) =>
                  t(`widget.social.searchType.${s}`)
                )}
                dropdownItemsData={[...searchType]}
                showArrow
                arrowPosition="right"
                handleClick={(item, data: (typeof SEARCH_TYPE)[number]) => {
                  if (data === 'CUSTOM') {
                    setSelectedTags([]);
                  } else {
                    setSearchValue('');
                    setSelectedTags(tags);
                  }
                  setTypology(data);
                }}
              >
                {t(`widget.social.searchType.${typology}`)}
              </DropdownMenu>

              <div className={'flex items-center flex-1 w-full'}>
                <ScrollArrows className="w-full">
                  {selectedTags.map((tag) => (
                    <Tag
                      key={tag}
                      type={'eye'}
                      disabled={disabledTags.includes(tag)}
                      onClick={() => {
                        handleDisabledTags(tag);
                      }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </ScrollArrows>

                {selectedTags.length > 0 && (
                  <p
                    className={`
                      text-sm
                      text-grey-50
                      flex-shrink-0
                      border-l
                      border-grey-10
                      h-full
                      flex
                      items-center
                      p-4
                      cursor-pointer
                    `}
                    onClick={removeSelectedTags}
                  >
                    Remove all tags
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className={'flex justify-center w-full h-full items-center'}>
          <Loading size={'large'} status={true} />
        </div>
      ) : wikipediaPages.length === 0 ? (
        <NoSearchResults />
      ) : (
        <div className={'overflow-y-scroll'}>
          <div className={`grid px-4 py-4 gap-4 grid-cols-${width}`}>
            {wikipediaPages.map((post) => (
              <WikipediaCard post={post} key={post.id} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};
